import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Info } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtNum } from "@/lib/format";

type Option = { code: string; action: string; points: number; unit?: string; manual_approval: boolean; annual_cap: number; granted_this_year: number; remaining: number | null };
const MANUAL = "__manual__";

// Entrega individual de puntos. Con una regla se aplica su tope anual (y se avisa antes
// si se va a recortar); el ajuste manual permite restar y no tiene tope.
export function GrantPointsDialog({ user, onClose, onDone }: { user: any | null; onClose: () => void; onDone: () => void }) {
  const [options, setOptions] = useState<Option[] | null>(null);
  const [mode, setMode] = useState<string>(MANUAL);
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setOptions(null); setMode(MANUAL); setPoints(""); setReason(""); setError("");
    api.get(`/admin/users/${user.id}/grant-options`).then((r) => {
      setOptions(r.data);
      if (r.data.length) { setMode(r.data[0].code); setPoints(String(r.data[0].points)); }
    }).catch(() => setOptions([]));
  }, [user]);

  const rule = options?.find((o) => o.code === mode);
  const n = Number(points);
  const valid = points !== "" && Number.isInteger(n) && n !== 0 && (mode === MANUAL || n > 0);
  const effective = rule && rule.remaining !== null ? Math.min(n, rule.remaining) : n;
  const balance = (user?.points || 0) + (valid ? effective : 0);

  const pick = (v: string) => {
    setMode(v); setError("");
    const r = options?.find((o) => o.code === v);
    setPoints(r ? String(r.points) : "");
  };

  const submit = async () => {
    if (!valid) { setError(mode === MANUAL ? "Escribe un número entero distinto de 0 (negativo para restar)." : "Escribe un número entero mayor que 0."); return; }
    if (balance < 0) { setError(`No puedes restar más de ${fmtNum(user.points)} puntos: el saldo quedaría en negativo.`); return; }
    if (rule && rule.remaining === 0) { setError(`${user.name} ya alcanzó el tope anual de “${rule.action}”.`); return; }
    setSaving(true);
    try {
      if (mode === MANUAL) {
        await api.post(`/admin/users/${user.id}/points`, { points: n, reason: reason.trim() || "Ajuste administrativo" });
        toast.success(`${n > 0 ? "Se sumaron" : "Se restaron"} ${fmtNum(Math.abs(n))} puntos a ${user.name}. Recibirá una notificación.`);
      } else {
        const { data } = await api.post(`/admin/users/${user.id}/grant`, { mechanism: mode, points: n, reason: reason.trim() || null });
        toast.success(data.granted < (data.requested ?? n)
          ? `Se entregaron ${fmtNum(data.granted)} de ${fmtNum(data.requested ?? n)} puntos: es lo que quedaba del tope anual.`
          : `Se entregaron ${fmtNum(data.granted)} puntos a ${user.name}. Recibirá una notificación y un correo.`);
      }
      onDone();
      onClose();
    } catch (err: any) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900" data-testid="grant-points-dialog">
        <DialogHeader><DialogTitle className="font-serif text-xl">Puntos para {user?.name}</DialogTitle></DialogHeader>
        {options === null ? (
          <div className="flex justify-center py-8" role="status"><Loader2 className="w-5 h-5 animate-spin text-gold-ink" aria-hidden="true" /><span className="sr-only">Cargando reglas…</span></div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Saldo actual: <strong className="font-mono text-slate-900">{fmtNum(user?.points)} pts</strong></p>
            <div>
              <Label htmlFor="grant-mode" className="text-slate-700 text-sm">¿Por qué entregas los puntos?</Label>
              <Select value={mode} onValueChange={pick}>
                <SelectTrigger id="grant-mode" data-testid="grant-mode-select" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
                  {options.map((o) => <SelectItem key={o.code} value={o.code}>{o.action} (+{fmtNum(o.points)})</SelectItem>)}
                  <SelectItem value={MANUAL}>Ajuste manual (sumar o restar)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {rule && (
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-1" data-testid="grant-cap-info">
                {rule.annual_cap > 0 ? (
                  <p>Tope anual: <strong>{fmtNum(rule.annual_cap)} pts</strong> · ya entregado este año: {fmtNum(rule.granted_this_year)} · <strong>disponible: {fmtNum(rule.remaining)}</strong></p>
                ) : <p>Esta regla no tiene tope anual.</p>}
                {rule.manual_approval && (
                  <p className="flex items-start gap-1.5 text-slate-600"><Info className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" /> Esta regla pide validar el caso antes de entregar los puntos.</p>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="grant-points" className="text-slate-700 text-sm">{mode === MANUAL ? "Puntos (negativo para restar)" : "Puntos"}</Label>
              <Input id="grant-points" data-testid="points-input" type="number" value={points} onChange={(e: any) => { setPoints(e.target.value); setError(""); }}
                aria-invalid={!!error} aria-describedby={error ? "grant-error" : undefined} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
            </div>
            <div>
              <Label htmlFor="grant-reason" className="text-slate-700 text-sm">Motivo (lo verá el cliente en sus notificaciones)</Label>
              <Input id="grant-reason" data-testid="reason-input" value={reason} onChange={(e: any) => setReason(e.target.value)}
                placeholder={rule ? rule.action : "Ej: Corrección de una carga anterior"} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
            </div>
            {valid && (
              <p className="text-sm text-slate-700" data-testid="grant-preview">
                {rule && rule.remaining !== null && n > rule.remaining
                  ? <>Solo se entregarán <strong>{fmtNum(effective)} pts</strong> (lo que queda del tope). </>
                  : null}
                El saldo quedará en <strong className="font-mono">{fmtNum(balance)} pts</strong>.
              </p>
            )}
            {error && <p id="grant-error" role="alert" className="text-sm text-red-700">{error}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
          <Button data-testid="save-points-button" onClick={submit} disabled={saving || options === null} className="gold-gradient-bg text-white font-semibold rounded-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" />
              : valid ? (n < 0 ? `Restar ${fmtNum(-n)} pts` : `Entregar ${fmtNum(effective)} pts`) : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
